// The "signed in, but not entitled" notice (PLATFORM-988). A 403 at /me is the authorizer's
// definitive "this identity has no access" (see redux/user.ts), so the session is signed out and the
// login page explains why, naming the account that was signed in. JumpCloud (or a client's IdP) may
// silently reuse its own session for a different account than the one typed, so naming it is what
// lets the user notice the wrong account.
//
// The email crosses the sign-out in sessionStorage, never the URL (it stays out of history and
// access logs): a federated sign-out navigates to the IdP logout URL and back, which reloads the
// SPA. The login page takes it exactly once.

const DENIED_EMAIL_STORAGE_KEY = 'auth_denied_email';

// Reads the `email` claim out of an ID token without verifying it — display only, never for a trust
// decision.
export const decodeJwtEmail = (idToken: string): string | null => {
  try {
    const payload = idToken.split('.')[1];
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return typeof decoded.email === 'string' && decoded.email ? decoded.email : null;
  } catch {
    return null;
  }
};

/** Records that the session was denied. Call after sign-out, which clears sessionStorage. */
export const recordAccessDenied = (email: string | null): void => {
  try {
    sessionStorage.setItem(DENIED_EMAIL_STORAGE_KEY, email ?? '');
  } catch {
    // sessionStorage unavailable (e.g. private mode): the user lands on the plain login page.
  }
};

/** The pending notice, if any. Doesn't remove it; see clearAccessDenied. */
export const peekAccessDenied = (): { email: string | null } | null => {
  try {
    const value = sessionStorage.getItem(DENIED_EMAIL_STORAGE_KEY);
    return value === null ? null : { email: value || null };
  } catch {
    return null;
  }
};

export const clearAccessDenied = (): void => {
  try {
    sessionStorage.removeItem(DENIED_EMAIL_STORAGE_KEY);
  } catch {
    // Nothing to clear.
  }
};

export const accessDeniedMessage = (email: string | null): string =>
  `${email ? `You're signed in as ${email}, which` : 'The account you signed in with'} isn't set up ` +
  "for access to Blisspoint. If that's the wrong account, sign out of JumpCloud (or your " +
  "company's sign-in) and try again.";
